import {
  Building2,
  CalendarDays,
  ChefHat,
  Grid3X3,
  LayoutDashboard,
  Package,
  Receipt,
  Settings2,
  Users,
  Trophy,
  type LucideIcon,
} from "lucide-react";
import type { AuthUser, ProviderApprovalStatus, ProviderMembershipRole, ProviderVenue, VenueApprovalStatus, WorkspaceSummary } from "../../lib/api";
import { type TranslationFunction, type TranslationKey, useAppPreferences } from "../../lib/preferences";

export type ProviderSection =
  | "dashboard"
  | "floor"
  | "orders"
  | "schedule"
  | "register"
  | "customers"
  | "tournaments"
  | "menu"
  | "venue-setup"
  | "business";

export const primarySections: Array<{ id: ProviderSection; label: string }> = [
  { id: "dashboard", label: "Dashboard" },
  { id: "floor", label: "Floor" },
  { id: "orders", label: "Orders" },
  { id: "schedule", label: "Bookings" },
  { id: "register", label: "Checkout" },
  { id: "customers", label: "Customers" },
  { id: "tournaments", label: "Tournaments" },
];

export const secondarySections: Array<{ id: ProviderSection; label: string }> = [
  { id: "menu", label: "Menu & stock" },
  { id: "venue-setup", label: "Venue Setup" },
  { id: "business", label: "Business" },
];

export const sectionIcons: Record<ProviderSection, LucideIcon | null> = {
  dashboard: LayoutDashboard,
  floor: Grid3X3,
  orders: ChefHat,
  schedule: CalendarDays,
  register: Receipt,
  customers: Users,
  tournaments: Trophy,
  menu: Package,
  "venue-setup": Building2,
  business: Settings2,
};

export const sectionOptions = [...primarySections, ...secondarySections];

export const sectionLabelKeys: Record<ProviderSection, TranslationKey> = {
  dashboard: "provider.sections.dashboard",
  floor: "provider.sections.floor",
  orders: "provider.sections.orders",
  schedule: "provider.sections.schedule",
  register: "provider.sections.register",
  customers: "provider.sections.customers",
  tournaments: "provider.sections.tournaments",
  menu: "provider.sections.menu",
  "venue-setup": "provider.sections.venueSetup",
  business: "provider.sections.business",
};

export const sectionCompactLabelKeys: Record<ProviderSection, string> = {
  dashboard: "provider.sections.compact.dashboard",
  floor: "provider.sections.compact.floor",
  orders: "provider.sections.compact.orders",
  schedule: "provider.sections.compact.schedule",
  register: "provider.sections.compact.register",
  customers: "provider.sections.compact.customers",
  tournaments: "provider.sections.compact.tournaments",
  menu: "provider.sections.compact.menu",
  "venue-setup": "provider.sections.compact.venueSetup",
  business: "provider.sections.compact.business",
};

export function formatLabel(value: string, t: TranslationFunction) {
  return t(value.replaceAll("_", " "));
}

export function countText(t: TranslationFunction, count: number, singular: string, plural: string) {
  return t(count === 1 ? singular : plural, { count });
}

export function roleLabel(role: string | undefined, t: TranslationFunction) {
  switch (role) {
    case "admin":
      return t("Admin");
    case "support":
      return t("Support");
    case "provider_owner":
      return t("Owner");
    case "provider_staff":
      return t("Staff");
    case "customer":
      return t("Customer");
    default:
      return t("Unknown role");
  }
}

export function membershipRoleLabel(role: string | null | undefined, t: TranslationFunction) {
  switch (role) {
    case "owner":
      return t("Owner");
    case "manager":
      return t("Manager");
    case "front_desk":
      return t("Front desk");
    default:
      return t("Unknown role");
  }
}

/** The 3-tier membership role of the signed-in member. Summaries cached before
 *  membership roles shipped carry no value, so fall back to the account-level
 *  role (staff conservatively map to the most restrictive tier). */
export function resolveProviderMembershipRole(
  summary: Pick<WorkspaceSummary, "membership_role">,
  user: AuthUser | null,
): ProviderMembershipRole {
  if (summary.membership_role) {
    return summary.membership_role;
  }

  return user?.role === "provider_owner" ? "owner" : "front_desk";
}

export function isProviderSection(value: string | null): value is ProviderSection {
  return (
    value === "dashboard" ||
    value === "floor" ||
    value === "orders" ||
    value === "schedule" ||
    value === "register" ||
    value === "customers" ||
    value === "tournaments" ||
    value === "menu" ||
    value === "venue-setup" ||
    value === "business"
  );
}

export function getApprovalTone(status: ProviderApprovalStatus) {
  switch (status) {
    case "approved":
      return "success";
    case "rejected":
      return "danger";
    case "suspended":
      return "warning";
    default:
      return "info";
  }
}

export function getVenueApprovalTone(status: VenueApprovalStatus) {
  switch (status) {
    case "approved":
      return "success";
    case "rejected":
    case "removed":
      return "danger";
    case "removal_requested":
      return "warning";
    default:
      return "info";
  }
}

export function venueCanConfigure(status: VenueApprovalStatus) {
  return status === "pending" || status === "approved" || status === "rejected";
}

export function venueCanOperate(status: VenueApprovalStatus) {
  return status === "approved" || status === "removal_requested";
}

export function VenueSwitcher({
  onSelect,
  selectedVenueId,
  venues,
}: {
  onSelect: (venueId: string) => void;
  selectedVenueId: string | null;
  venues: ProviderVenue[];
}) {
  const { t } = useAppPreferences();

  if (venues.length === 0) {
    return <span className="ops-chip">{t("No venue selected")}</span>;
  }

  return (
    <label className="ops-field">
      <span className="sr-only">{t("Active venue")}</span>
      <select
        className="ops-select"
        onChange={(event) => onSelect(event.target.value)}
        title={t("Active venue")}
        value={selectedVenueId ?? venues[0]?.id ?? ""}
      >
        {venues.map((venue) => (
          <option key={venue.id} value={venue.id}>
            {venue.name}
          </option>
        ))}
      </select>
    </label>
  );
}
